import { useState, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import useAuth from "../../../../hooks/useAuth";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../../ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../../ui/alert-dialog";
import { config } from "../../../../Environment";
import { Button } from "../../../ui/button";
import { Input } from "../../../ui/input";
import { Label } from "../../../ui/label";
import { Badge } from "../../../ui/badge";
import { Separator } from "../../../ui/separator";
import { Alert, AlertDescription } from "../../../ui/alert";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../ui/select";
import { RadioGroup, RadioGroupItem } from "../../../ui/radio-group";
import useRetryFetch from "../../../../hooks/useRetryFetch";
import { useEffectOnce } from "../../../../hooks/useEffectOnce";
import { toast } from "sonner";
import {
  Loader2,
  UserPlus,
  Mail,
  CheckCircle2,
  Clock,
  Users,
  Send,
  AlertCircle,
  ShieldCheck,
  User,
  Link2,
  Copy,
  Globe,
  Lock,
  UserCheck,
  X,
  Building2,
  FolderTree,
} from "lucide-react";
import {
  shareWithOrganization,
  shareWithCollection,
  getWorkflowSharing,
  unshareFromOrganization,
  unshareFromCollection,
} from "../../../../api/workflowSharing";
import type { Organization, Collection } from "../../../../lib/types";

interface WorkflowPermissionDialogProps {
  open: boolean;
  onOpenChange: (value: boolean) => void;
  workflowId: string;
}

interface WorkflowUser {
  _id: string;
  fiUser: {
    _id: string;
    displayName?: string;
    firstName?: string;
    lastName?: string;
    email: string;
  };
}

interface InvitedUser {
  email: string;
}

interface WorkflowMetadata {
  fiExpert?: {
    _id: string;
    displayName?: string;
    firstName?: string;
    lastName?: string;
    email: string;
  };
}

interface WorkflowPermission {
  accessMode: 'public' | 'client' | 'restricted';
}

const WorkflowPermissionDialogContent = ({
  workflowId,
}: {
  workflowId: string;
}) => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [workflowMetadata, setWorkflowMetadata] = useState<WorkflowMetadata | null>(null);
  const [workflowPermission, setWorkflowPermission] = useState<WorkflowPermission | null>(null);
  const [workflowUsers, setWorkflowUsers] = useState<WorkflowUser[] | null>(null);
  const [invitedUsers, setInvitedUsers] = useState<InvitedUser[] | null>(null);
  const [loading, setLoading] = useState<{ [key: string]: boolean }>({});
  const isLoading = useMemo<boolean>(() => {
    return Object.values(loading).some((status) => status);
  }, [loading]);
  const [shareWithUserVisible, setShareWithUserVisible] =
    useState<boolean>(false);
  const [shareWithUserEmail, setShareWithUserEmail] = useState<string>("");
  const [accessMode, setAccessMode] = useState<'public' | 'client' | 'restricted'>('client');
  const [isCopied, setIsCopied] = useState(false);
  const [userToRemove, setUserToRemove] = useState<WorkflowUser | null>(null);

  // Organization/Collection sharing state
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [collections, setCollections] = useState<Collection[]>([]);
  const [sharedOrganizations, setSharedOrganizations] = useState<Organization[]>([]);
  const [sharedCollections, setSharedCollections] = useState<Collection[]>([]);
  const [selectedOrgId, setSelectedOrgId] = useState<string>("");
  const [selectedCollectionId, setSelectedCollectionId] = useState<string>("");
  const [shareOrgCollectionVisible, setShareOrgCollectionVisible] = useState<boolean>(false);
  const [shareType, setShareType] = useState<'organization' | 'collection'>('collection');

  const retryFetch = useRetryFetch();

  async function loadWorkflowMetadata() {
    try {
      setLoading((prev) => ({ ...prev, loadWorkflowMetadata: true }));
      const response = await retryFetch(
        `${config.API_BASE}/api/prescriptions/${encodeURIComponent(workflowId)}/metadata?includeExpert=1`
      );
      const result = await response.json();
      setWorkflowMetadata(result);
    } catch (err) {
      console.log(`[WorkflowPermissionDialog] loadWorkflowMetadata error`, err);
    } finally {
      setLoading((prev) => ({ ...prev, loadWorkflowMetadata: false }));
    }
  }

  async function loadWorkflowUsers() {
    try {
      setLoading((prev) => ({ ...prev, loadWorkflowUsers: true }));
      const response = await retryFetch(
        `${config.API_BASE}/api/author/workflow/user/search?workflowId=${encodeURIComponent(workflowId)}&source=shared`
      );
      const result = await response.json();
      if (Array.isArray(result.list)) {
        setWorkflowUsers(result.list);
      }
    } catch (err) {
      console.log(`[WorkflowPermissionDialog] loadWorkflowUsers error`, err);
    } finally {
      setLoading((prev) => ({ ...prev, loadWorkflowUsers: false }));
    }
  }

  async function loadInvitedUsers() {
    try {
      setLoading((prev) => ({ ...prev, loadInvitedUsers: true }));
      const response = await retryFetch(
        `${config.API_BASE}/api/invitation/search?sent=1&action=run_workflow&state=pending&contextKey=context.workflowId&contextValue=${encodeURIComponent(workflowId)}`
      );
      const result = await response.json();
      if (Array.isArray(result.list)) {
        setInvitedUsers(result.list);
      }
    } catch (err) {
      console.log(`[WorkflowPermissionDialog] loadInvitedUsers error`, err);
    } finally {
      setLoading((prev) => ({ ...prev, loadInvitedUsers: false }));
    }
  }

  async function loadWorkflowPermission() {
    try {
      setLoading((prev) => ({ ...prev, loadWorkflowPermission: true }));
      const response = await retryFetch(
        `${config.API_BASE}/api/author/workflow/permission?workflowId=${encodeURIComponent(workflowId)}`
      );
      if (response.ok) {
        const result = await response.json();
        setWorkflowPermission(result);
        setAccessMode(result.accessMode || 'client');
      }
    } catch (err) {
      console.log(`[WorkflowPermissionDialog] loadWorkflowPermission error`, err);
    } finally {
      setLoading((prev) => ({ ...prev, loadWorkflowPermission: false }));
    }
  }

  async function loadOrganizations() {
    try {
      setLoading((prev) => ({ ...prev, loadOrganizations: true }));
      const response = await retryFetch(`${config.API_BASE}/api/organizations`);
      if (response.ok) {
        const data = await response.json();
        // Filter to only organizations where user is admin or author
        const userOrgs = data.filter((org: Organization) =>
          org.fiOrgAdmins?.some((admin: any) =>
            (admin._id || admin).toString() === user?._id?.toString()
          )
        );
        setOrganizations(userOrgs);
      }
    } catch (err) {
      console.log(`[WorkflowPermissionDialog] loadOrganizations error`, err);
    } finally {
      setLoading((prev) => ({ ...prev, loadOrganizations: false }));
    }
  }

  async function loadCollections() {
    if (!selectedOrgId) return;

    try {
      setLoading((prev) => ({ ...prev, loadCollections: true }));
      const response = await retryFetch(`${config.API_BASE}/api/collections/${selectedOrgId}`);
      if (response.ok) {
        const data = await response.json();
        setCollections(data);
      }
    } catch (err) {
      console.log(`[WorkflowPermissionDialog] loadCollections error`, err);
    } finally {
      setLoading((prev) => ({ ...prev, loadCollections: false }));
    }
  }

  async function loadWorkflowSharing() {
    try {
      setLoading((prev) => ({ ...prev, loadWorkflowSharing: true }));
      const data = await getWorkflowSharing(workflowId, retryFetch);
      setSharedOrganizations(data.organizations || []);
      setSharedCollections(data.collections || []);
    } catch (err) {
      console.log(`[WorkflowPermissionDialog] loadWorkflowSharing error`, err);
    } finally {
      setLoading((prev) => ({ ...prev, loadWorkflowSharing: false }));
    }
  }

  async function updateAccessMode(newAccessMode: 'public' | 'client' | 'restricted') {
    try {
      setLoading((prev) => ({ ...prev, updateAccessMode: true }));
      const response = await retryFetch(
        `${config.API_BASE}/api/author/workflow/permission`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({
            workflowId,
            accessMode: newAccessMode,
          }),
        }
      );
      if (response.ok) {
        setAccessMode(newAccessMode);
        toast.success(
          t("access_mode_updated") || "Link sharing permissions updated successfully!"
        );
      } else {
        toast.error(t("failed_to_update_access_mode") || "Failed to update permissions");
      }
    } catch (err) {
      console.log(`[WorkflowPermissionDialog] updateAccessMode error`, err);
      toast.error(t("failed_to_update_access_mode") || "Failed to update permissions");
    } finally {
      setLoading((prev) => ({ ...prev, updateAccessMode: false }));
    }
  }

  async function shareWithUser() {
    try {
      const email = shareWithUserEmail.trim();
      if (!email) {
        toast.error(t("email_required") || "Email is required");
        return;
      }

      // Basic email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        toast.error(t("invalid_email") || "Invalid email address");
        return;
      }

      setLoading((prev) => ({ ...prev, shareWithUser: true }));
      const response = await retryFetch(
        `${config.API_BASE}/api/author/workflow/share?workflowId=${encodeURIComponent(workflowId)}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({
            email,
          }),
        }
      );
      const result = await response.json();

      if (result.type === "status") {
        if (result.status === "SHARED") {
          toast.success(
            t("activity_shared_successfully") || "Activity shared successfully!"
          );
          await loadWorkflowUsers();
        } else if (result.status === "INVITED") {
          toast.success(
            t("invitation_sent_successfully") ||
              "Invitation sent successfully!"
          );
          await loadInvitedUsers();
        } else {
          toast.error(t("error") || "An error occurred");
        }
        setShareWithUserVisible(false);
        setShareWithUserEmail("");
      }
    } catch (err) {
      console.log(`[WorkflowPermissionDialog] shareWithUser error`, err);
      toast.error(t("failed_to_send_invitation") || "Failed to send invitation");
    } finally {
      setLoading((prev) => ({ ...prev, shareWithUser: false }));
    }
  }

  async function unshareUser(userId: string) {
    try {
      setLoading((prev) => ({ ...prev, unshareUser: true }));
      const response = await retryFetch(
        `${config.API_BASE}/api/author/workflow/unshare`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({
            workflowId,
            userId,
          }),
        }
      );

      if (response.ok) {
        const result = await response.json();
        if (result.type === "status" && result.status === "UNSHARED") {
          toast.success(
            t("user_removed_successfully") ||
              "User access has been revoked successfully!"
          );
          await loadWorkflowUsers();
        } else {
          toast.error(t("failed_to_remove_user") || "Failed to remove user");
        }
      } else if (response.status === 404) {
        toast.error(
          t("user_not_found_in_shared_list") ||
            "User not found in shared list"
        );
      } else {
        toast.error(t("failed_to_remove_user") || "Failed to remove user");
      }
    } catch (err) {
      console.log(`[WorkflowPermissionDialog] unshareUser error`, err);
      toast.error(t("failed_to_remove_user") || "Failed to remove user");
    } finally {
      setLoading((prev) => ({ ...prev, unshareUser: false }));
      setUserToRemove(null);
    }
  }

  async function shareWithOrgOrCollection() {
    try {
      if (shareType === 'organization' && !selectedOrgId) {
        toast.error(t("please_select_organization") || "Please select an organization");
        return;
      }
      if (shareType === 'collection' && !selectedCollectionId) {
        toast.error(t("please_select_group") || "Please select a group");
        return;
      }

      setLoading((prev) => ({ ...prev, shareOrgCollection: true }));

      if (shareType === 'organization') {
        await shareWithOrganization(workflowId, selectedOrgId, retryFetch);
        toast.success(t("activity_shared_with_organization") || "Activity shared with organization successfully!");
      } else {
        await shareWithCollection(workflowId, selectedCollectionId, retryFetch);
        toast.success(t("activity_shared_with_group") || "Activity shared with group successfully!");
      }

      await loadWorkflowSharing();
      setShareOrgCollectionVisible(false);
      setSelectedOrgId("");
      setSelectedCollectionId("");
    } catch (err) {
      console.log(`[WorkflowPermissionDialog] shareWithOrgOrCollection error`, err);
      toast.error(t("failed_to_share") || "Failed to share activity");
    } finally {
      setLoading((prev) => ({ ...prev, shareOrgCollection: false }));
    }
  }

  async function unshareOrganization(orgId: string) {
    try {
      setLoading((prev) => ({ ...prev, unshareOrganization: true }));
      await unshareFromOrganization(workflowId, orgId, retryFetch);
      toast.success(t("organization_access_removed") || "Organization access removed successfully!");
      await loadWorkflowSharing();
    } catch (err) {
      console.log(`[WorkflowPermissionDialog] unshareOrganization error`, err);
      toast.error(t("failed_to_remove_access") || "Failed to remove access");
    } finally {
      setLoading((prev) => ({ ...prev, unshareOrganization: false }));
    }
  }

  async function unshareCollection(collectionId: string) {
    try {
      setLoading((prev) => ({ ...prev, unshareCollection: true }));
      await unshareFromCollection(workflowId, collectionId, retryFetch);
      toast.success(t("group_access_removed") || "Group access removed successfully!");
      await loadWorkflowSharing();
    } catch (err) {
      console.log(`[WorkflowPermissionDialog] unshareCollection error`, err);
      toast.error(t("failed_to_remove_access") || "Failed to remove access");
    } finally {
      setLoading((prev) => ({ ...prev, unshareCollection: false }));
    }
  }

  async function assignExpert(email: string) {
    try {
      console.log(
        `[WorkflowPermissionDialog] assignExpert email ${JSON.stringify(email)}`
      );
      setLoading((prev) => ({ ...prev, assignExpert: true }));
      const response = await retryFetch(
        `${config.API_BASE}/api/author/workflow/edit?workflowId=${encodeURIComponent(workflowId)}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({
            expert: email, // email address to assign expert or empty string to remove expert
          }),
        }
      );
      const result = await response.json();
      if (result.type === "status") {
        if (result.status === "UPDATED") {
          toast.success(t("expert_assigned_successfully") || "Expert assigned successfully!");
          await loadWorkflowMetadata();
        } else {
          toast.error(t("msg_error_generic") || "An error occurred");
        }
      }
    } catch (err) {
      console.log(`[WorkflowPermissionDialog] assignExpert error`, err);
      toast.error(t("failed_to_assign_expert") || "Failed to assign expert");
    } finally {
      setLoading((prev) => ({ ...prev, assignExpert: false }));
    }
  }

  const expertOptions = useMemo(() => {
    const options: Array<{ value: string; text: string; email: string }> = [];
    // author can also assign themselves as expert, so start with author's name
    if (user._id && user.email) {
      const authorDisplayName =
        user.displayName ?? `${user.firstName} ${user.lastName}`;
      options.push({
        value: user._id,
        text: `${authorDisplayName} (${user.email})`,
        email: user.email,
      });
    }
    workflowUsers?.forEach((item) => {
      const userId = item.fiUser?._id;
      const displayName =
        item.fiUser?.displayName ??
        `${item.fiUser?.firstName} ${item.fiUser?.lastName}`;
      const email = item.fiUser?.email;
      if (userId && email) {
        options.push({
          value: userId,
          text: `${displayName} (${email})`,
          email: email,
        });
      }
    });
    return options;
  }, [workflowUsers, user]);

  async function selectExpert(expertUserId: string) {
    // author may designate themselves or one of their workflow users as expert
    const selectedOption = expertOptions.find(
      (option) => option.value === expertUserId
    );
    if (selectedOption) {
      await assignExpert(selectedOption.email);
    }
  }

  const handleCopyLink = async () => {
    const shareUrl = `${window.location.origin}/share/activity?wid=${workflowId}`;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setIsCopied(true);
      toast.success(t("link_copied") || "Link copied to clipboard!");
      setTimeout(() => setIsCopied(false), 2000);
    } catch (error) {
      console.error("Failed to copy link:", error);
      toast.error(t("failed_to_copy") || "Failed to copy link");
    }
  };

  // Load collections when organization is selected
  useEffect(() => {
    if (selectedOrgId) {
      loadCollections();
    } else {
      setCollections([]);
    }
  }, [selectedOrgId]);

  useEffectOnce(() => {
    (async function () {
      await loadWorkflowMetadata();
      await loadWorkflowPermission();
      await loadWorkflowUsers();
      await loadInvitedUsers();
      await loadOrganizations();
      await loadWorkflowSharing();
    })();
  }, []);

  return (
    <div className="space-y-6">
      {/* Link Sharing Section */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Link2 className="w-5 h-5 text-gray-600" />
          <h3 className="font-semibold text-gray-900">
            {t("link_sharing") || "Link Sharing"}
          </h3>
        </div>

        {loading.loadWorkflowPermission ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          </div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-3">
              <Label className="text-sm font-medium">
                {t("who_can_access_link") || "Who can access with the link?"}
              </Label>
              <RadioGroup
                value={accessMode}
                onValueChange={(value: 'public' | 'client' | 'restricted') => updateAccessMode(value)}
                disabled={loading.updateAccessMode}
                className="space-y-3"
              >
                <div className="flex items-start space-x-3 p-3 rounded-lg border hover:bg-gray-50 transition-colors">
                  <RadioGroupItem value="public" id="access-public" className="mt-0.5" />
                  <div className="flex-1">
                    <Label htmlFor="access-public" className="flex items-center gap-2 cursor-pointer font-medium">
                      <Globe className="w-4 h-4 text-primary" />
                      {t("anyone_with_link") || "Anyone with the link"}
                    </Label>
                    <p className="text-xs text-gray-600 mt-1">
                      {t("anyone_with_link_desc") || "Anyone on the internet with this link can run the activity"}
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-3 p-3 rounded-lg border hover:bg-gray-50 transition-colors">
                  <RadioGroupItem value="client" id="access-client" className="mt-0.5" />
                  <div className="flex-1">
                    <Label htmlFor="access-client" className="flex items-center gap-2 cursor-pointer font-medium">
                      <UserCheck className="w-4 h-4 text-green-600" />
                      {t("anyone_with_account") || "Anyone with an account"}
                    </Label>
                    <p className="text-xs text-gray-600 mt-1">
                      {t("anyone_with_account_desc") || "Anyone who is logged in can run the activity"}
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-3 p-3 rounded-lg border hover:bg-gray-50 transition-colors">
                  <RadioGroupItem value="restricted" id="access-restricted" className="mt-0.5" />
                  <div className="flex-1">
                    <Label htmlFor="access-restricted" className="flex items-center gap-2 cursor-pointer font-medium">
                      <Lock className="w-4 h-4 text-orange-600" />
                      {t("only_specific_people") || "Only specific people"}
                    </Label>
                    <p className="text-xs text-gray-600 mt-1">
                      {t("only_specific_people_desc") || "Only people you've shared with can access"}
                    </p>
                  </div>
                </div>
              </RadioGroup>
            </div>

            {/* Copy Link Button */}
            <div className="flex gap-2">
              <Input
                value={`${window.location.origin}/share/activity?wid=${workflowId}`}
                readOnly
                className="flex-1 text-sm"
                onClick={(e) => e.currentTarget.select()}
              />
              <Button
                onClick={handleCopyLink}
                variant="outline"
                size="icon"
                className="flex-shrink-0"
              >
                {isCopied ? (
                  <CheckCircle2 className="w-4 h-4 text-green-600" />
                ) : (
                  <Copy className="w-4 h-4" />
                )}
              </Button>
            </div>

            {/* Info Alert */}
            {accessMode === 'restricted' && (
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription className="text-sm">
                  {t("restricted_mode_info") ||
                    "In restricted mode, only users you've explicitly shared with below can access this activity via the link."}
                </AlertDescription>
              </Alert>
            )}
          </div>
        )}
      </div>

      <Separator />

      {/* Organization/Class Sharing Section */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FolderTree className="w-5 h-5 text-gray-600" />
            <h3 className="font-semibold text-gray-900">
              {t("share_with_groups") || "Share with Classes & Organizations"}
            </h3>
          </div>
          {organizations.length > 0 && (
            <Button
              size="sm"
              onClick={() => setShareOrgCollectionVisible(true)}
              disabled={shareOrgCollectionVisible || isLoading}
            >
              <Building2 className="w-4 h-4 mr-2" />
              {t("share") || "Share"}
            </Button>
          )}
        </div>

        {organizations.length === 0 ? (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              {t("no_organizations_as_admin") ||
                "You are not an administrator of any organizations. Organization admins can share activities with entire organizations or specific classes."}
            </AlertDescription>
          </Alert>
        ) : (
          <>

            {/* Share Form */}
            {shareOrgCollectionVisible && (
              <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 space-y-3">
                <div className="space-y-2">
                  <Label>{t("share_type") || "Share Type"}</Label>
                  <RadioGroup
                    value={shareType}
                    onValueChange={(value: 'organization' | 'collection') => {
                      setShareType(value);
                      setSelectedOrgId("");
                      setSelectedCollectionId("");
                    }}
                    className="flex gap-4"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="collection" id="type-collection" />
                      <Label htmlFor="type-collection" className="cursor-pointer font-normal">
                        {t("class_group") || "Class/Group"}
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="organization" id="type-organization" />
                      <Label htmlFor="type-organization" className="cursor-pointer font-normal">
                        {t("entire_organization") || "Entire Organization"}
                      </Label>
                    </div>
                  </RadioGroup>
                </div>

                {shareType === 'organization' ? (
                  <div className="space-y-2">
                    <Label>{t("select_organization") || "Select Organization"}</Label>
                    <Select value={selectedOrgId} onValueChange={setSelectedOrgId}>
                      <SelectTrigger>
                        <SelectValue placeholder={t("select_organization") || "Select organization..."} />
                      </SelectTrigger>
                      <SelectContent>
                        {organizations.map((org) => (
                          <SelectItem key={org._id} value={org._id}>
                            {org.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ) : (
                  <>
                    <div className="space-y-2">
                      <Label>{t("select_organization") || "Select Organization"}</Label>
                      <Select value={selectedOrgId} onValueChange={setSelectedOrgId}>
                        <SelectTrigger>
                          <SelectValue placeholder={t("select_organization") || "Select organization..."} />
                        </SelectTrigger>
                        <SelectContent>
                          {organizations.map((org) => (
                            <SelectItem key={org._id} value={org._id}>
                              {org.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    {selectedOrgId && (
                      <div className="space-y-2">
                        <Label>{t("select_group") || "Select Group/Class"}</Label>
                        {loading.loadCollections ? (
                          <div className="flex items-center justify-center py-4">
                            <Loader2 className="w-4 h-4 animate-spin" />
                          </div>
                        ) : (
                          <Select value={selectedCollectionId} onValueChange={setSelectedCollectionId}>
                            <SelectTrigger>
                              <SelectValue placeholder={t("select_group") || "Select group/class..."} />
                            </SelectTrigger>
                            <SelectContent>
                              {collections.map((coll) => (
                                <SelectItem key={coll._id} value={coll._id}>
                                  {coll.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      </div>
                    )}
                  </>
                )}

                <div className="flex gap-2">
                  <Button
                    onClick={shareWithOrgOrCollection}
                    disabled={
                      (shareType === 'organization' ? !selectedOrgId : !selectedCollectionId) ||
                      loading.shareOrgCollection
                    }
                    className="flex-1"
                  >
                    {loading.shareOrgCollection ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4 mr-2" />
                    )}
                    {t("share") || "Share"}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShareOrgCollectionVisible(false);
                      setSelectedOrgId("");
                      setSelectedCollectionId("");
                    }}
                    disabled={loading.shareOrgCollection}
                  >
                    {t("cancel") || "Cancel"}
                  </Button>
                </div>
              </div>
            )}

            {/* Shared Organizations/Collections List */}
            {loading.loadWorkflowSharing ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-primary" />
              </div>
            ) : (sharedOrganizations.length > 0 || sharedCollections.length > 0) ? (
              <div className="space-y-2">
                {/* Shared Organizations */}
                {sharedOrganizations.map((org) => (
                  <div
                    key={org._id}
                    className="flex items-center gap-3 p-3 bg-purple-50 border border-purple-200 rounded-lg"
                  >
                    <div className="rounded-full bg-purple-100 p-2">
                      <Building2 className="w-4 h-4 text-purple-600" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">{org.name}</p>
                      <p className="text-xs text-gray-600">{t("organization") || "Organization"}</p>
                    </div>
                    <Badge className="bg-purple-100 text-purple-800 border-purple-200">
                      {t("shared") || "Shared"}
                    </Badge>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => unshareOrganization(org._id)}
                      disabled={loading.unshareOrganization}
                      className="h-8 w-8 text-gray-500 hover:text-red-600 hover:bg-red-50"
                      title={t("remove_access") || "Remove access"}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}

                {/* Shared Collections */}
                {sharedCollections.map((coll) => (
                  <div
                    key={coll._id}
                    className="flex items-center gap-3 p-3 bg-primary/5 border border-primary/20 rounded-lg"
                  >
                    <div className="rounded-full bg-primary/10 p-2">
                      <FolderTree className="w-4 h-4 text-primary" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">{coll.name}</p>
                      <p className="text-xs text-gray-600">
                        {coll.level === 10 ? t("department") : coll.level === 20 ? t("course") : t("class")}
                      </p>
                    </div>
                    <Badge className="bg-primary/10 text-primary border-primary/20">
                      {t("shared") || "Shared"}
                    </Badge>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => unshareCollection(coll._id)}
                      disabled={loading.unshareCollection}
                      className="h-8 w-8 text-gray-500 hover:text-red-600 hover:bg-red-50"
                      title={t("remove_access") || "Remove access"}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  {t("not_shared_with_organizations") ||
                    "This activity is not currently shared with any organizations or groups."}
                </AlertDescription>
              </Alert>
            )}
          </>
        )}
      </div>

      <Separator />

      {/* Shared Users Section */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5 text-gray-600" />
            <h3 className="font-semibold text-gray-900">
              {t("share_to_dashboard") || "Add to User's Dashboard"}
            </h3>
          </div>
          <Button
            size="sm"
            onClick={() => setShareWithUserVisible(true)}
            disabled={shareWithUserVisible || isLoading}
          >
            <UserPlus className="w-4 h-4 mr-2" />
            {t("share") || "Share"}
          </Button>
        </div>

        {/* Share Form */}
        {shareWithUserVisible && (
          <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 space-y-3">
            <div className="space-y-2">
              <Label htmlFor="shareWithUserEmail">
                {t("email_address") || "Email Address"}
              </Label>
              <Input
                id="shareWithUserEmail"
                type="email"
                placeholder={t("enter_email") || "Enter email address"}
                value={shareWithUserEmail}
                onChange={(e) => setShareWithUserEmail(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    shareWithUser();
                  }
                }}
                autoFocus
              />
            </div>
            <div className="flex gap-2">
              <Button
                onClick={shareWithUser}
                disabled={
                  !shareWithUserEmail.trim() || loading.shareWithUser
                }
                className="flex-1"
              >
                {loading.shareWithUser ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                {t("send_invitation") || "Send Invitation"}
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setShareWithUserVisible(false);
                  setShareWithUserEmail("");
                }}
                disabled={loading.shareWithUser}
              >
                {t("cancel") || "Cancel"}
              </Button>
            </div>
          </div>
        )}

        {/* Shared Users List */}
        {loading.loadWorkflowUsers ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          </div>
        ) : workflowUsers && workflowUsers.length > 0 ? (
          <div className="space-y-2">
            {workflowUsers.map((item) => (
              <div
                key={item._id}
                className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-lg"
              >
                <div className="rounded-full bg-green-100 p-2">
                  <CheckCircle2 className="w-4 h-4 text-green-600" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">
                    {item.fiUser.displayName ??
                      `${item.fiUser.firstName} ${item.fiUser.lastName}`}
                  </p>
                  <p className="text-xs text-gray-600">{item.fiUser.email}</p>
                </div>
                <Badge className="bg-green-100 text-green-800 border-green-200">
                  {t("active") || "Active"}
                </Badge>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setUserToRemove(item)}
                  disabled={loading.unshareUser}
                  className="h-8 w-8 text-gray-500 hover:text-red-600 hover:bg-red-50"
                  title={t("remove_access") || "Remove access"}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        ) : (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              {t("workflow_not_shared") ||
                "This activity is not currently shared with anyone."}
            </AlertDescription>
          </Alert>
        )}
      </div>

      <Separator />

      {/* Pending Invitations Section */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Clock className="w-5 h-5 text-gray-600" />
          <h3 className="font-semibold text-gray-900">
            {t("pending_invitations") || "Pending Invitations"}
          </h3>
        </div>

        {loading.loadInvitedUsers ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          </div>
        ) : invitedUsers && invitedUsers.length > 0 ? (
          <div className="space-y-2">
            {invitedUsers.map((item) => (
              <div
                key={item.email}
                className="flex items-center gap-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg"
              >
                <div className="rounded-full bg-yellow-100 p-2">
                  <Mail className="w-4 h-4 text-yellow-600" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">
                    {item.email}
                  </p>
                  <p className="text-xs text-gray-600">
                    {t("invitation_pending") || "Invitation pending"}
                  </p>
                </div>
                <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200">
                  {t("pending") || "Pending"}
                </Badge>
              </div>
            ))}
          </div>
        ) : (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              {t("no_pending_invitations") || "No pending invitations."}
            </AlertDescription>
          </Alert>
        )}
      </div>

      <Separator />

      {/* Expert Assignment Section */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-gray-600" />
          <h3 className="font-semibold text-gray-900">
            {t("expert_assignment") || "Expert Assignment"}
          </h3>
        </div>

        {loading.loadWorkflowMetadata ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          </div>
        ) : (
          <div className="space-y-3">
            <div className="space-y-2">
              <Label htmlFor="expertSelect">
                {t("assign_expert") || "Assign Expert"}
              </Label>
              <Select
                value={workflowMetadata?.fiExpert?._id || ""}
                onValueChange={selectExpert}
                disabled={loading.assignExpert || expertOptions.length === 0}
              >
                <SelectTrigger id="expertSelect">
                  <SelectValue
                    placeholder={t("select_expert") || "Select an expert..."}
                  />
                </SelectTrigger>
                <SelectContent>
                  {expertOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.text}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Current Expert Display */}
            {workflowMetadata?.fiExpert && (
              <div className="flex items-center gap-3 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                <div className="rounded-full bg-purple-100 p-2">
                  <User className="w-4 h-4 text-purple-600" />
                </div>
                <div className="flex-1">
                  <p className="text-xs text-gray-600 mb-1">
                    {t("current_expert") || "Current Expert"}
                  </p>
                  <p className="text-sm font-medium text-gray-900">
                    {workflowMetadata.fiExpert.displayName ??
                      `${workflowMetadata.fiExpert.firstName} ${workflowMetadata.fiExpert.lastName}`}
                  </p>
                  <p className="text-xs text-gray-600">
                    {workflowMetadata.fiExpert.email}
                  </p>
                </div>
                <Badge className="bg-purple-100 text-purple-800 border-purple-200">
                  {t("expert") || "Expert"}
                </Badge>
              </div>
            )}

            {!workflowMetadata?.fiExpert && expertOptions.length === 0 && (
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  {t("no_users_available_for_expert") ||
                    "No users available to assign as expert. Share this activity first."}
                </AlertDescription>
              </Alert>
            )}
          </div>
        )}
      </div>

      {/* Loading Indicator */}
      {isLoading && (
        <div className="flex items-center justify-center py-2">
          <Loader2 className="w-5 h-5 animate-spin text-primary mr-2" />
          <span className="text-sm text-muted-foreground">
            {t("loading") || "Loading..."}
          </span>
        </div>
      )}

      {/* Confirmation Dialog */}
      <AlertDialog
        open={userToRemove !== null}
        onOpenChange={(open) => !open && setUserToRemove(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("confirm_remove_user") || "Remove User Access?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("confirm_remove_user_description") ||
                `Are you sure you want to remove ${
                  userToRemove?.fiUser.displayName ??
                  `${userToRemove?.fiUser.firstName} ${userToRemove?.fiUser.lastName}`
                }'s access to this activity? They will no longer be able to see it in their dashboard.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={loading.unshareUser}>
              {t("cancel") || "Cancel"}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => userToRemove && unshareUser(userToRemove.fiUser._id)}
              disabled={loading.unshareUser}
              className="bg-red-600 hover:bg-red-700"
            >
              {loading.unshareUser ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t("removing") || "Removing..."}
                </>
              ) : (
                t("remove") || "Remove"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

function WorkflowPermissionDialog({
  open,
  onOpenChange,
  workflowId,
}: WorkflowPermissionDialogProps) {
  const { t } = useTranslation();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {t("manage_permissions") || "Share & Permissions"}
          </DialogTitle>
          <DialogDescription>
            {t("manage_permissions_description") ||
              "Control who can access this activity via link, add it to specific users' dashboards, and assign an expert."}
          </DialogDescription>
        </DialogHeader>
        <WorkflowPermissionDialogContent workflowId={workflowId} />
      </DialogContent>
    </Dialog>
  );
}

export default WorkflowPermissionDialog;
